import { useState } from "react";
import { GroupHeadcount, GroupHeadcountMonth, GroupHeadcountResult, GroupValues, historyNote } from "models/hr-dashboard";
import MonthHeader from "./month-header";
import { GroupNameCell, subtotalRowClass, useCollapsedDivisions } from "./group-hierarchy";

interface GroupHeadcountTableProps {
    data: GroupHeadcountResult;
}

const DETAIL_ROWS: { label: string; key: keyof GroupValues; ytd?: boolean }[] = [
    { label: "HC beginning", key: "hcBegin" },
    { label: "Hired", key: "hired", ytd: true },
    { label: "Left", key: "left", ytd: true },
    { label: "Transfers (net)", key: "transfers", ytd: true },
    { label: "Balance end of month", key: "hcEnd" },
];

const HiddenCell = ({ min, colSpan }: { min: number; colSpan?: number }) => (
    <td colSpan={colSpan} className="text-center text-muted" title={`Hidden: group smaller than ${min} people`}>—</td>
);

const value = (m: GroupHeadcountMonth, key: keyof GroupValues) => (m.values ? m.values[key] : "—");
const totalBalance = (m: { values: { hcEnd: number } | null }) => (m.values ? m.values.hcEnd : "—");

// Pivot: one row per department (or division) with the balance at the end of each month.
// Department view: each division is a subtotal row (can be collapsed) with its departments below it.
// "Detail" shows HC beginning / Hired / Left / Transfers / Balance for each group.
const GroupHeadcountTable = ({ data }: GroupHeadcountTableProps) => {
    const [detail, setDetail] = useState(false);
    const { collapsed, toggle, isVisible } = useCollapsedDivisions();
    const groupLabel = data.groupBy === "division" ? "Division" : "Department";
    const groups = data.groups.filter(isVisible);

    const pivotRow = (g: GroupHeadcount) => (
        <tr key={`${g.kind}-${g.name}`} className={subtotalRowClass(g)}>
            <GroupNameCell group={g} collapsed={collapsed} toggle={toggle} />
            {g.hidden
                ? <HiddenCell min={data.smallGroupMin} colSpan={data.total.months.length + 2} />
                : <>
                    {g.months.map(m => <td key={m.key} className="text-end">{value(m, "hcEnd")}</td>)}
                    <td className="text-end">{g.ytd?.hired ?? ""}</td>
                    <td className="text-end">{g.ytd?.left ?? ""}</td>
                </>}
        </tr>
    );

    const detailRows = (g: GroupHeadcount) => DETAIL_ROWS.map((row, i) => (
        <tr
            key={`${g.kind}-${g.name}-${row.key}`}
            className={[g.kind === "subtotal" ? "table-light" : "", row.key === "hcEnd" ? "fw-bold" : ""].join(" ").trim() || undefined}
        >
            {i === 0 && <GroupNameCell group={g} collapsed={collapsed} toggle={toggle} rowSpan={DETAIL_ROWS.length} />}
            <td>{row.label}</td>
            {g.hidden
                ? (i === 0 ? <HiddenCell min={data.smallGroupMin} colSpan={data.total.months.length + 1} /> : null)
                : <>
                    {g.months.map(m => <td key={m.key} className="text-end">{value(m, row.key)}</td>)}
                    <td className="text-end">{row.ytd && g.ytd ? g.ytd[row.key] : ""}</td>
                </>}
        </tr>
    ));

    return (
        <>
            <div className="form-check form-switch mb-2">
                <input className="form-check-input" type="checkbox" id="hrGroupDetail" checked={detail} onChange={() => setDetail(!detail)} />
                <label className="form-check-label" htmlFor="hrGroupDetail">Detail (HC beginning, hired, left and transfers per {groupLabel.toLowerCase()})</label>
            </div>
            <div className="table-responsive">
                {detail ? (
                    <table className="table table-sm table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>{groupLabel}</th>
                                <th></th>
                                {data.total.months.map(m => <th key={m.key} className="text-end">{m.label}</th>)}
                                <th className="text-end">YTD</th>
                            </tr>
                        </thead>
                        <tbody>{groups.map(detailRows)}</tbody>
                    </table>
                ) : (
                    <table className="table table-sm table-bordered mb-0">
                        <MonthHeader months={data.total.months} fiscalYear={data.fiscalYear} label={`${groupLabel} (balance end of month)`} trailing={["YTD Hired", "YTD Left"]} />
                        <tbody>
                            {groups.map(pivotRow)}
                            <tr className="fw-bold">
                                <td>Total</td>
                                {data.total.months.map(m => <td key={m.key} className="text-end">{totalBalance(m)}</td>)}
                                <td className="text-end">{data.total.ytd?.hired ?? ""}</td>
                                <td className="text-end">{data.total.ytd?.left ?? ""}</td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>
            <p className="small text-muted mt-2 mb-0">
                {data.groupBy === "department" && "Divisions show the subtotal of their departments; click the arrow to hide or show them. "}
                {historyNote(data.historyStart, groupLabel.toLowerCase())}
            </p>
        </>
    );
};

export default GroupHeadcountTable;
